import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import LandingV2 from "@/components/landing/LandingV2";
import { breadcrumbJsonLd } from "@/config/seoConfig";
import { markDegraded, seoHead, SITE_BASE_URL } from "@/lib/seo/head";
import { orgRef } from "@/lib/seo/graph";
import { introPassPriceINR } from "@/hooks/useIntroPassPlan";
import { getPublicCourses } from "@/lib/publicCourses.functions";
import { getPublicLandingTrialCourse } from "@/lib/landingTrial.functions";
import { getPublicSubscriptionPlans } from "@/lib/subscriptionPlans.functions";
import { publicSubscriptionPlansQuery } from "@/lib/subscriptionPlansQuery";
import { withBudget } from "@/lib/serverMemo";
import landingCriticalCss from "@/components/landing/landing-critical.generated.css?inline";

/**
 * One shareable landing page per course. Same layout, buttons and checkout as
 * the homepage; only the hero shows this course. Prices come from admin config.
 */
export const Route = createFileRoute("/courses/$slug")({
  loader: async ({ params }) => {
    // Only the course is critical. Trial/plan data get a short budget and
    // resolve to null when slow — the page then renders without SSR pricing or
    // Offer schema, and the client fetches live prices as it already does.
    const [levels, trial, plans] = await Promise.all([
      getPublicCourses(),
      withBudget(getPublicLandingTrialCourse(), 1_200, null),
      withBudget(getPublicSubscriptionPlans(), 1_200, null),
    ]);
    // An empty catalogue means the lookup failed with no known-good copy: that
    // is an error (not cached), never a false 404 for a real course.
    if (!levels.length) throw new Error("course catalogue unavailable");
    const course = levels.flatMap((l) => l.courses).find((c) => c.slug === params.slug);
    if (!course) throw notFound();
    return {
      course,
      trialCourse: trial ?? null,
      plans: plans && plans.length ? plans : null,
    };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return seoHead({ title: "Course not found", noindex: true });
    const { course } = loaderData;
    const description = courseMetaDescription(course);
    const comingSoon = course.status === "coming_soon";
    const url = `${SITE_BASE_URL}/courses/${course.slug}`;
    // Offer price comes only from the live plan catalogue; if it can't be read
    // server-side (or the course isn't on sale) the Offer is omitted.
    const passPrice = comingSoon
      ? null
      : introPassPriceINR(loaderData.plans?.find((p) => p.is_intro_pass === true) ?? null);
    const seo = seoHead({
      title: course.title.length + 16 <= 60 ? `${course.title} Course for Kids` : course.title,
      description,
      canonical: `/courses/${course.slug}`,
      noindex: comingSoon,
      follow: comingSoon,
      ogImage: course.thumbnailUrl?.startsWith("https://") ? course.thumbnailUrl : undefined,
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Home", url: "/" },
          { name: "Courses", url: "/courses" },
          { name: course.title, url: `/courses/${course.slug}` },
        ]),
        {
          "@type": "Course",
          "@id": `${url}#course`,
          name: course.title,
          description,
          url,
          ...(course.thumbnailUrl ? { image: course.thumbnailUrl } : {}),
          inLanguage: "en-IN",
          educationalLevel: `Children aged ${course.minAge ?? 8}-${course.maxAge ?? 14}`,
          ...(course.learningPoints.length ? { teaches: course.learningPoints } : {}),
          provider: orgRef,
          hasCourseInstance: { "@type": "CourseInstance", courseMode: "Online" },
          ...(passPrice
            ? {
                offers: {
                  "@type": "Offer",
                  category: "Paid",
                  price: passPrice,
                  priceCurrency: "INR",
                  availability: "https://schema.org/InStock",
                  url,
                  seller: orgRef,
                },
              }
            : {}),
        },
      ],
    });
    // Missing live prices = fallback render; keep it out of the page cache.
    return { ...markDegraded(seo, !comingSoon && loaderData.plans === null), styles: [{ children: landingCriticalCss }] };
  },
  notFoundComponent: CourseNotFound,
  errorComponent: () => (
    <main className="container mx-auto px-4 py-16 text-center">
      <h1 className="lx-h1 mb-3">This course is briefly unavailable</h1>
      <a className="underline" href="/courses">See all courses</a>
    </main>
  ),
  component: CourseLandingRoute,
});

/** Trim to `max` chars at a sentence end, else a word boundary (never mid-word). */
function clip(text: string, max = 155): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const sentence = cut.lastIndexOf(". ");
  if (sentence >= 100) return cut.slice(0, sentence + 1);
  return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:\s]+$/, "")}…`;
}

/**
 * Parent-facing meta description (~120-155 chars) built only from the course's
 * own title, tagline, description and learning points.
 */
function courseMetaDescription(course: {
  title: string;
  tagline: string | null;
  description: string | null;
  learningPoints: string[];
  minAge: number | null;
  maxAge: number | null;
}): string {
  const ages = `${course.minAge ?? 8}-${course.maxAge ?? 14}`;
  const lead = (course.tagline || "").trim().replace(/[.!?]*$/, "");
  const points = course.learningPoints.slice(0, 2).map((p) => p.trim().replace(/[.!?]*$/, "").replace(/^./, (c) => c.toLowerCase()));
  const desc = (course.description || "").trim();
  const opener = lead || (desc.length >= 80 ? "" : course.title);
  const generic = `A guided online life skills course for Indian children aged ${ages}.`;
  const candidates = [
    ...[2, 1].filter((n) => points.length >= n).map((n) => `A guided online course for kids aged ${ages}: ${points.slice(0, n).join(" and ")}.`),
    generic,
  ].map((body) => (opener ? `${opener}. ${body}` : desc || body));
  return candidates.find((c) => c.length <= 155) ?? clip(candidates[candidates.length - 1]!);
}

function CourseNotFound() {
  return (
    <main className="container mx-auto px-4 py-16 text-center">
      <h1 className="lx-h1 mb-3">Course not found</h1>
      <a className="underline" href="/courses">See all courses</a>
    </main>
  );
}

function CourseLandingRoute() {
  const { course, trialCourse, plans } = Route.useLoaderData();
  const queryClient = useQueryClient();
  if (plans && !queryClient.getQueryData(publicSubscriptionPlansQuery.queryKey)) {
    queryClient.setQueryData(publicSubscriptionPlansQuery.queryKey, plans);
  }
  return <LandingV2 initialTrialCourse={trialCourse} course={course} />;
}
